import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Package, ArrowLeft, Shield, CheckCircle2, IndianRupee } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contribute = () => {
  const [stats, setStats] = useState({ total_supported: 1240, kits_distributed: 850, total_impact_funds: 0 });
  const [amount, setAmount] = useState('50');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/impact/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDonate = async () => {
    try {
      const donationAmount = parseFloat(amount);
      if (isNaN(donationAmount) || donationAmount <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }

      setLoading(true);

      const response = await axios.post(`${BACKEND_URL}/api/payments/razorpay/create-donation`, {
        amount: donationAmount,
        currency: 'INR',
        impact_category: 'menstrual_awareness',
        payment_method: 'razorpay'
      });

      const options = {
        key: response.data.key_id,
        amount: response.data.amount,
        currency: response.data.currency,
        order_id: response.data.order_id,
        name: "Ovalu Impact",
        description: "Support Menstrual Health Awareness",
        image: "https://customer-assets.emergentagent.com/job_ovalu-design/artifacts/231iio5y_1000063769.jpg",
        handler: async function (razorpayResponse) {
          try {
            await axios.post(`${BACKEND_URL}/api/payments/razorpay/verify-donation`, {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature
            });
            toast.success("Thank you for your contribution! ✨");
            fetchStats();
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        theme: { color: "#FF6B9D" }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Error in donation:', error);
      const detail = error.response?.data?.detail || error.message || "Unknown Error";
      toast.error(`Initiation Failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F8] to-white">
      <Navbar />

      <div className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-block bg-pink-100 text-[#FF6B9D] px-6 py-2 rounded-full font-bold text-sm mb-6">
              ❤️ OVALU IMPACT MISSION
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
              Small Change. <span className="text-[#FF6B9D]">Big Impact.</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Help us end period stigma and provide hygiene resources to rural girls across India.
              Together, we can build a world where every girl feels empowered.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Impact Meter Side */}
            <div className="space-y-8">
              <Card className="p-8 border-pink-100 bg-white/50 backdrop-blur-sm shadow-2xl rounded-[3rem]">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                  <Heart className="w-8 h-8 text-[#FF6B9D] mr-3 fill-[#FF6B9D]" />
                  Live Impact Meter
                </h2>

                <div className="grid sm:grid-cols-2 gap-6 mb-10">
                  <div className="p-6 bg-pink-50 rounded-[2rem] border border-pink-100">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                      <Users className="w-6 h-6 text-[#FF6B9D]" />
                    </div>
                    <div className="text-4xl font-black text-gray-900 mb-1">
                      {stats.total_supported.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">Girls Supported</div>
                  </div>

                  <div className="p-6 bg-purple-50 rounded-[2rem] border border-purple-100">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                      <Package className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="text-4xl font-black text-gray-900 mb-1">
                      {stats.kits_distributed.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">Hygiene Kits Distributed</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between text-sm font-bold text-gray-700">
                    <span>Current Goal: Support 5,000 Girls</span>
                    <span>{Math.round((stats.total_supported / 5000) * 100)}%</span>
                  </div>
                  <Progress value={(stats.total_supported / 5000) * 100} className="h-4 bg-gray-100" />
                  <p className="text-xs text-gray-500 text-center">Every contribution moves us closer to our goal ✨</p>
                </div>
              </Card>

              {/* Trust Section */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 flex items-center gap-3 bg-green-50 rounded-2xl border border-green-100">
                  <Shield className="w-6 h-6 text-green-600" />
                  <span className="text-xs font-bold text-green-800">100% Secure Payments</span>
                </div>
                <div className="p-4 flex items-center gap-3 bg-blue-50 rounded-2xl border border-blue-100">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                  <span className="text-xs font-bold text-blue-800">Verified NGO Partners</span>
                </div>
              </div>
            </div>

            {/* Donation Side */}
            <Card className="p-8 lg:p-12 border-pink-100 shadow-2xl rounded-[3rem] sticky top-32">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Contribution</h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {['10', '50', '100', '500', '1000'].map((val) => (
                  <button
                    key={val}
                    onClick={() => { setAmount(val); setIsCustom(false); }}
                    className={`p-4 rounded-2xl border-2 transition-all font-bold text-lg ${!isCustom && amount === val
                        ? 'border-[#FF6B9D] bg-pink-50 text-[#FF6B9D]'
                        : 'border-gray-100 text-gray-600 hover:border-pink-200'
                      }`}
                  >
                    ₹{val}
                  </button>
                ))}
                <button
                  onClick={() => setIsCustom(true)}
                  className={`p-4 rounded-2xl border-2 transition-all font-bold ${isCustom ? 'border-[#FF6B9D] bg-pink-50 text-[#FF6B9D]' : 'border-gray-100 text-gray-600 hover:border-pink-200'
                    }`}
                >
                  Custom
                </button>
              </div>

              {isCustom && (
                <div className="relative mb-8">
                  <IndianRupee className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-12 h-14 rounded-2xl border-pink-100 focus:border-[#FF6B9D] text-lg font-bold"
                  />
                </div>
              )}

              <div className="p-6 bg-pink-50/50 rounded-2xl mb-8 border border-pink-100">
                <div className="text-[#FF6B9D] font-bold text-sm mb-2 flex items-center">
                  🔥 Impact of ₹{amount || '0'}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {amount === '10' ? "₹10 provides menstrual awareness resources for 1 girl in a rural school." :
                    amount === '50' ? "₹50 provides a complete hygiene kit (pads, soap, guide) for 1 girl." :
                      amount === '100' ? "₹100 supports 2 girls with hygiene kits and follow-up consultation." :
                        amount === '500' ? "₹500 sponsors a community awareness workshop for 15+ families." :
                          "Your contribution helps provide free health consultations and resources to women in need across our partner networks."}
                </p>
              </div>

              <Button
                onClick={handleDonate}
                disabled={loading}
                className="w-full h-16 bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white rounded-2xl text-xl font-bold shadow-lg shadow-pink-200 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                {loading ? "Processing..." : `Contribute ₹${amount || '0'} Now`}
              </Button>

              <div className="mt-6 text-center text-xs text-gray-500">
                100% of your donation goes directly to the Ovalu Impact Fund 🌸
              </div>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contribute;
